import { CircleUserRound, UsersRound } from 'lucide-react';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { useERSContext } from '@/context';

export const Profile = ({ selectedChat }) => {
  const { t } = useTranslation();
  const { users } = useERSContext();

  // Look up the enriched user from context (has merged profile data like username)
  const enrichedUser = useMemo(() => {
    if (!selectedChat || selectedChat.type === 'group') return null;
    return users.find(u => u.id === selectedChat.id);
  }, [users, selectedChat]);
  // Split display name into serial ID and incident type
  const splitDisplayName = displayName => {
    if (!displayName) return { serialId: '', incidentType: '' };

    const parts = displayName.split(' - ');
    if (parts.length === 2) {
      return { serialId: parts[0], incidentType: parts[1] };
    }
    return { serialId: '', incidentType: displayName };
  };

  // Get translated incident type from translation files
  const getTranslatedIncidentType = incidentType => {
    if (!incidentType) return '';

    // Get all incident types from translation
    const incidentTypes = t('INCIDENT_TYPES', { returnObjects: true });
    if (!Array.isArray(incidentTypes)) return incidentType;

    // Find the matching incident type and return its translated label
    const matchedType = incidentTypes.find(type => type.value === incidentType);
    return matchedType?.label || incidentType;
  };

  // Get translated display name using translation files
  const getTranslatedDisplayName = () => {
    if (!selectedChat?.displayName) return '';

    const { serialId, incidentType } = splitDisplayName(selectedChat.displayName);

    if (!incidentType) return selectedChat.displayName;

    const translatedIncidentType = getTranslatedIncidentType(incidentType);

    return serialId ? `${serialId} - ${translatedIncidentType}` : translatedIncidentType;
  };

  if (!selectedChat) return null;

  const isGroup = selectedChat.type === 'group';
  const translatedDisplayName = getTranslatedDisplayName();

  return (
    <div className="flex flex-col items-center gap-1 w-full h-full p-4">
      {isGroup ? <UsersRound size={20} absoluteStrokeWidth /> : <CircleUserRound size={40} absoluteStrokeWidth />}

      <span className="text-lg font-bold">{translatedDisplayName}</span>

      {/* Group Members Count */}
      {isGroup && (
        <span className="text-sm font-normal text-placeholder">
          {selectedChat?.members ? selectedChat?.members?.size : '0'} {t('PROFILE_PANEL.MEMBERS_LABEL')}
        </span>
      )}

      {/* Group or Individual Profile */}
      {isGroup ? (
        <div className="w-full flex flex-col flex-1 min-h-0 mt-4">
          <h3 className="font-semibold mb-3 flex-shrink-0">{t('PROFILE_PANEL.GROUP_MEMBERS_TITLE')}</h3>

          <div className="flex-1 overflow-y-auto border-b rounded-md min-h-0">
            {selectedChat?.members &&
              Array.from(selectedChat?.members.values()).map(member => (
                <div key={member.jid} className={cn('flex items-center gap-3 p-2 border-b')}>
                  <CircleUserRound size={20} absoluteStrokeWidth />
                  <div className="flex flex-col items-start">
                    <span className="font-medium text-sm">
                      {member.nick || t('PROFILE_PANEL.MEMBER_FALLBACK_NAME')}
                    </span>
                    <span className="text-xs text-placeholder">
                      {users.find(u => u.id === member.jid)?.username ||
                        member.jid ||
                        t('PROFILE_PANEL.MEMBER_FALLBACK_ID')}
                    </span>
                  </div>
                </div>
              ))}
          </div>
        </div>
      ) : (
        <div className="w-full mt-6 px-6 flex flex-col gap-4">
          <div className="flex flex-col items-start">
            <span className="font-medium text-sm">{t('PROFILE_PANEL.USERNAME_LABEL')}</span>
            <span className="font-normal text-sm text-placeholder">{enrichedUser?.username || selectedChat.id}</span>
          </div>

          <div className="flex flex-col items-start">
            <span className="font-medium text-sm">{t('PROFILE_PANEL.EMAIL_LABEL')}</span>
            <span className="font-normal text-sm text-placeholder">{selectedChat.email || '--'}</span>
          </div>
        </div>
      )}
    </div>
  );
};
